import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Smartphone, Sparkles, BookOpen } from "lucide-react";

const HeroSection = () => {
  return (
    <section className="min-h-screen pt-24 pb-16 bg-gradient-to-b from-background via-secondary/10 to-background flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">AI-Powered Study Companion</span>
            </motion.div>
            
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-foreground mb-6 leading-tight">
              <span className="bg-gradient-to-r from-primary via-purple-500 to-accent bg-clip-text text-transparent">
                Zuto
              </span>
              <br />
              <span className="text-foreground">Your Smart Lecture</span>
              <br />
              <span className="text-foreground">Companion</span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-4 leading-relaxed">
              Focus on learning instead of frantic note-taking. Record live lectures, paste YouTube links, or upload audio—Zuto transforms any audio into clean, structured study material.
            </p>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-10">
              So you can pay attention in class, watch online courses, and still have everything you need to revise later.
            </p>
            
            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
              <a 
                href="https://apps.apple.com/us/app/zuto-ai-assistant/id6752733945" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <Button 
                  size="lg"
                  className="w-full sm:w-auto bg-black hover:bg-black/90 text-white px-8 py-6 text-lg flex items-center gap-3 group"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs font-normal leading-none">Download on the</div>
                    <div className="text-lg font-semibold leading-tight">App Store</div>
                  </div>
                </Button>
              </a>
              
              <a 
                href="https://play.google.com/store/apps/details?id=ai.zuto.app" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <Button 
                  size="lg"
                  className="w-full sm:w-auto bg-black hover:bg-black/90 text-white px-8 py-6 text-lg flex items-center gap-3 group"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs font-normal leading-none">Get it on</div>
                    <div className="text-lg font-semibold leading-tight">Google Play</div>
                  </div>
                </Button>
              </a>
            </div>

            {/* Key Features Preview */}
            <div className="grid grid-cols-3 gap-6 max-w-md mx-auto lg:mx-0">
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-primary mb-1">3-in-1</div>
                <div className="text-sm text-muted-foreground">Notes, Revise, Q&A</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-primary mb-1">AI</div>
                <div className="text-sm text-muted-foreground">Powered</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-bold text-primary mb-1">Cloud</div>
                <div className="text-sm text-muted-foreground">Sync</div>
              </div>
            </div>
          </motion.div>

          {/* Right side - Visual/Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Background gradient circles */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-full blur-3xl"></div>
              
              {/* Main visual container */}
              <div className="relative bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-12 border border-border/50 backdrop-blur-sm">
                <div className="space-y-8">
                  {/* Feature cards */}
                  <div className="bg-background/80 rounded-xl p-6 border border-border/50 backdrop-blur-sm">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">AI Notes</div>
                        <div className="text-sm text-muted-foreground">Auto-generated from lectures</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-background/80 rounded-xl p-6 border border-border/50 backdrop-blur-sm">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">Smart Revision</div>
                        <div className="text-sm text-muted-foreground">Highlights & explanations</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-background/80 rounded-xl p-6 border border-border/50 backdrop-blur-sm">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">Cross-Platform</div>
                        <div className="text-sm text-muted-foreground">Sync across devices</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
